import { Wallet } from 'lucide-react'
import { useState, type FormEvent } from 'react'

import type { AccountsPayablePurchase, CreateAccountsPayablePaymentPayload } from '../../../services/purchases.service'
import { Button } from '../../ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog'
import { Input } from '../../ui/input'
import { formatPayableDate, formatPayableMoney, parsePayableAmount } from '../accounts-payable'

interface AddPurchasePaymentModalProps {
  purchase: AccountsPayablePurchase
  onClose: () => void
  onRegister: (payment: CreateAccountsPayablePaymentPayload) => Promise<void>
  isSubmitting: boolean
}

function today() {
  const date = new Date()
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

export function AddPurchasePaymentModal({ purchase, onClose, onRegister, isSubmitting }: AddPurchasePaymentModalProps) {
  const [amount, setAmount] = useState('')
  const [date, setDate] = useState(today)
  const [concept, setConcept] = useState('')
  const remaining = Number(purchase.balance_amount)
  const amountValue = parsePayableAmount(amount)
  const amountError = !amount.trim() ? null : amountValue === null
    ? 'Ingresa un importe mayor a cero, con máximo dos decimales.'
    : amountValue > remaining ? 'El abono no puede superar el saldo pendiente.' : null
  const canRegister = amountValue !== null && amountValue <= remaining && Boolean(date)

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (!canRegister || amountValue === null || isSubmitting) return
    await onRegister({ amount: amountValue, payment_date: date, concept: concept.trim() || null })
  }

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogContent className="max-h-[90vh] gap-3 overflow-y-auto p-5 sm:max-w-xl">
        <DialogHeader className="pr-8">
          <DialogTitle className="flex items-center gap-2">
            <Wallet className="h-5 w-5 text-primary" /> Agregar abono
          </DialogTitle>
          <DialogDescription>{purchase.reference_number} · {purchase.supplier.name}</DialogDescription>
        </DialogHeader>

        <div className="rounded-lg border border-border bg-card p-4">
          <div className="mb-4 flex flex-wrap justify-between gap-2 text-sm text-card-description">
            <span>{purchase.sucursal.name}</span><span>Compra del {formatPayableDate(purchase.purchase_date)}</span>
          </div>
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div><dt className="text-xs text-card-description">Total de compra</dt><dd className="mt-1 font-semibold tabular-nums text-card-foreground">{formatPayableMoney(purchase.total_amount, purchase.currency)}</dd></div>
            <div><dt className="text-xs text-card-description">Abonado</dt><dd className="mt-1 font-semibold tabular-nums text-card-foreground">{formatPayableMoney(purchase.paid_amount, purchase.currency)}</dd></div>
            <div><dt className="text-xs text-card-description">Saldo pendiente</dt><dd className="mt-1 font-semibold tabular-nums text-primary">{formatPayableMoney(remaining, purchase.currency)}</dd></div>
          </dl>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <label htmlFor="purchase-payment-amount" className="text-sm font-medium">Monto del abono <span className="text-red-500">*</span></label>
              <Input
                id="purchase-payment-amount" inputMode="decimal" placeholder="0.00" autoFocus required
                value={amount} onChange={(event) => setAmount(event.target.value)}
                aria-invalid={Boolean(amountError)} aria-describedby="purchase-payment-amount-help"
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="purchase-payment-date" className="text-sm font-medium">Fecha del abono <span className="text-red-500">*</span></label>
              <Input id="purchase-payment-date" type="date" required value={date} onChange={(event) => setDate(event.target.value)} />
            </div>
          </div>
          <div className="flex flex-wrap items-start justify-between gap-2">
            <p id="purchase-payment-amount-help" role={amountError ? 'alert' : undefined} className={`text-xs ${amountError ? 'text-destructive' : 'text-card-description'}`}>
              {amountError || `Máximo disponible: ${formatPayableMoney(remaining, purchase.currency)}`}
            </p>
            <Button type="button" variant="ghost" size="sm" className="h-auto p-0 text-primary" onClick={() => setAmount(remaining.toFixed(2))}>
              Usar saldo completo
            </Button>
          </div>

          <div className="space-y-2">
            <label htmlFor="purchase-payment-concept" className="text-sm font-medium">Concepto</label>
            <Input id="purchase-payment-concept" maxLength={100} placeholder="Concepto del abono" value={concept} onChange={(event) => setConcept(event.target.value)} />
          </div>

          <div className="rounded-lg border border-primary/20 bg-primary/5 p-4" aria-live="polite">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-sm text-card-foreground">Saldo después del abono</span>
              <strong className="text-lg tabular-nums text-card-foreground">{canRegister && amountValue !== null ? formatPayableMoney(remaining - amountValue, purchase.currency) : '—'}</strong>
            </div>
            {canRegister && amountValue === remaining && <p className="mt-2 text-xs text-primary">La compra quedará liquidada.</p>}
          </div>

          <DialogFooter className="border-t border-border pt-4">
            <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>Cancelar</Button>
            <Button type="submit" disabled={!canRegister || isSubmitting}>{isSubmitting ? 'Registrando...' : 'Registrar abono'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
